// Global
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { ReactElement, useEffect, useCallback, useRef, useMemo } from 'react';
import { ProductAndArticleComponent } from 'lib/templates/Feature.Dart.model';
import { ItemEx } from 'lib/templates/_.Sitecore.Override';

export type ProductInformation = ItemEx &
  ProductAndArticleComponent.ProductInformation.ProductInformation;

const BazaarvoiceScript = (): ReactElement => {
  const context = useSitecoreContext();
  const { productInformation } = context?.sitecoreContext?.route?.fields || {};
  const prodInfo = productInformation as ProductInformation;
  const clientName = (context?.sitecoreContext?.bazaarVoice as { clientName?: string })?.clientName;
  const environment = (context?.sitecoreContext?.bazaarVoice as { environment?: string })
    ?.environment;
  const locale = (context?.sitecoreContext?.bazaarVoice as { locale?: string })?.locale;
  const siteID = (context?.sitecoreContext?.bazaarVoice as { siteID?: string })?.siteID;

  const observerRef = useRef<MutationObserver | null>(null);

  // Memoize productId
  const productId = useMemo(() => {
    return `${(prodInfo?.fields?.oldPageId?.value || prodInfo?.id)
      ?.replace(/-/g, '')
      .toUpperCase()}`;
  }, [prodInfo?.fields?.oldPageId?.value, prodInfo?.id]);

  // Function to set the product ID attribute
  const setProductIdAttribute = useCallback(() => {
    const element = document.getElementById('bv-reviewdisplay');
    if (element && productId) {
      element.setAttribute('data-bv-product-id', productId);
      return true;
    }
    return false;
  }, [productId]);

  // Function to setup MutationObserver to watch for the element
  const setupElementObserver = useCallback(() => {
    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Try to set attribute immediately
    if (setProductIdAttribute()) {
      return;
    }

    // If element doesn't exist, observe for it
    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the added node is our target or contains it
              if (element.id === 'bv-reviewdisplay' || element.querySelector('#bv-reviewdisplay')) {
                if (setProductIdAttribute() && observerRef.current) {
                  observerRef.current.disconnect();
                  observerRef.current = null;
                }
                return;
              }
            }
          }
        }
      }
    });

    // Observe the entire document body for changes
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }, [setProductIdAttribute]);

  useEffect(() => {
    if (!clientName || !siteID || !environment || !locale) {
      return;
    }

    const scriptSrc = `https://apps.bazaarvoice.com/deployments/${clientName}/${siteID}/${environment}/${locale}/bv.js`;

    // Check if script already exists to avoid duplicates
    const existingScript = document.head.querySelector(`script[src="${scriptSrc}"]`);

    let script: HTMLScriptElement;

    if (!existingScript) {
      script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;
      script.setAttribute('data-bazaarvoice-component', 'true');

      script.onload = () => {
        setTimeout(() => {
          setupElementObserver();
        }, 100);
      };

      document.head.appendChild(script);
    } else {
      script = existingScript as HTMLScriptElement;
      setupElementObserver();
    }

    // Cleanup function
    return () => {
      // Disconnect observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      const ourScript = document.head.querySelector(
        `script[src="${scriptSrc}"][data-bazaarvoice-component="true"]`
      );
      if (ourScript && ourScript.parentNode) {
        ourScript.parentNode.removeChild(ourScript);
      }

      const element = document.getElementById('bv-reviewdisplay');
      if (element) {
        element.removeAttribute('data-bv-product-id');
      }
    };
  }, [clientName, siteID, environment, locale, setupElementObserver]);

  // Handle productId changes
  useEffect(() => {
    if (productId) {
      setupElementObserver();
    }
  }, [productId, setupElementObserver]);

  return <></>;
};

export default BazaarvoiceScript;
